import { GA_LABEL_ENUMS, triggerGAEvent } from "../../constant/google-analytics/GAEvents";
import { VOICE_MODE, VOICE_NOTES } from "../../constant/voiceMode";
import { iChatData } from "../../types";
import { SearchProgressStore } from "../../services/searchProgress/searchProgressStore";
import { ChatRequest, MaskedRequest, TypedTurn } from "../../services/voice/voiceBus";
import { joinFragments } from "../../services/voice/liveTranscript";
import { VoiceFrame, isUserSpeaking, planVoiceFrame, shouldDropNext } from "../../services/voice/voiceFrames";
import type { Turn, VoiceSession } from "./types";
import { VoiceRuntime, VOICE_EXCHANGE, voiceLog, now, sendAppend } from "./runtime";
import { withFailedExchange, withCleanPrompts } from "./chatEntries";
import { endOpening } from "./opening";
import { requestSource, VoiceAnalytics } from "./voiceAnalytics";
import type { TranscriptIO } from "./transcriptIO";
import type { Bubbles } from "./bubbles";

export const createNarration = (rt: VoiceRuntime, transcriptIO: TranscriptIO, voiceAnalytics: VoiceAnalytics, bubbles: Bubbles) => {
  const { live, sessionRef, turnRef, repliesRef, handlers, setVoiceRequestOpen } = rt;
  const { writeLine, writeDelegation, adoptChatId, cleanerOf } = transcriptIO;
  const { trackAnswered } = voiceAnalytics;
  const { applyReplies } = bubbles;

  const delegationFor = (turn: Turn | null): string | null => {
    const session = sessionRef.current;
    return turn?.delegation && session?.id && turn.liveSessionId === session.id ? turn.delegation.id : null;
  };

  const finishNarration = (turn: Turn | null) => {
    if (!turn || !turn.voice || turn.narrated) return;
    turn.narrated = true;
    turn.says = [];
    turn.next = null;
    trackAnswered(turn);
    const words = joinFragments(turn.words);
    if (!words || !turn.cleaner || !live.current.state.chatData.some((entry: iChatData) => entry.id === turn.userEntryId)) return;
    turn.cleaner.finish("assistant", turn.words);
    repliesRef.current.push({ turn, cleaner: turn.cleaner });
    applyReplies();
  };

  const pumpSays = (session: VoiceSession) => {
    const turn = turnRef.current;
    if (!turn || turn.says.length === 0 || session.startedAt === null) return;
    if (isUserSpeaking(now(), session.lastUserAt, VOICE_MODE.quietMs)) return;
    if (turn.lastSaySentAt !== null && session.lastAssistantAt !== null && now() - session.lastAssistantAt < VOICE_MODE.quietMs) return;
    const delegationId = delegationFor(turn);
    turn.says.splice(0).forEach((say) => {
      const eventId = sendAppend(session, "session.commentary.append", delegationId, say);
      if (eventId && turn.timing && !turn.timing.ack) session.acks.set(eventId, turn);
    });
    endOpening(session);
    turn.lastSaySentAt = now();
    session.transcript.markSaySent();
    session.windowTurn = turn;
  };

  const closeNarration = (session: VoiceSession, at: number) => {
    const turn = turnRef.current;
    if (!turn || !turn.voice || turn.open || turn.narrated || turn.says.length > 0) return;
    const quietSince = Math.max(turn.lastSaySentAt ?? turn.handOffAt, session.lastAssistantAt ?? 0);
    if (at - quietSince < VOICE_MODE.exchangeSettleMs) return;
    if (turn.next && !turn.stopped) {
      const next = turn.next;
      turn.next = null;
      if (!shouldDropNext(session.lastUserAt, turn.lastSaySentAt ?? turn.handOffAt)) {
        sendAppend(session, "session.commentary.append", delegationFor(turn), next);
        turn.lastSaySentAt = at;
        session.transcript.markSaySent();
        session.windowTurn = turn;
        return;
      }
      voiceLog("next.dropped", { traceId: turn.traceId });
    }
    const late = joinFragments(turn.late);
    if (late && !turn.stopped && !turn.failed) {
      sendAppend(session, "session.thinking.append", delegationFor(turn), VOICE_NOTES.lateWords(late));
      voiceLog("request.late", { traceId: turn.traceId, chars: late.length });
      session.answer = { turn, fragments: [], shown: null };
    }
    const heard = joinFragments(turn.heard);
    if (heard && !turn.stopped && !turn.failed) {
      sendAppend(session, "session.thinking.append", delegationFor(turn), VOICE_NOTES.heard(heard));
      voiceLog("request.heard", { traceId: turn.traceId, chars: heard.length });
      session.stats.heard += 1;
      session.answer = { turn, fragments: [], shown: null };
    }
    finishNarration(turn);
  };

  const pumpProgress = (session: VoiceSession, at: number) => {
    const turn = turnRef.current;
    if (!turn || !turn.voice || !turn.open || turn.stopped || turn.progressDone) return;
    const { steps, messages } = SearchProgressStore.getSnapshot();
    if (messages.length < turn.progressSeen) turn.progressSeen = 0;
    messages.slice(turn.progressSeen).forEach((message) => turn.progressQueue.push(message.text));
    turn.progressSeen = messages.length;
    steps.forEach((step) => {
      const label = turn.stepLabels.get(step.id);
      if (!step.text || label?.text === step.text) return;
      turn.stepLabels.set(step.id, { text: step.text, since: at, relabelled: !!label, said: false });
    });
    if (isUserSpeaking(at, session.lastUserAt, VOICE_MODE.quietMs)) return;
    if (session.lastAssistantAt !== null && at - session.lastAssistantAt < VOICE_MODE.quietMs) return;
    const due = steps.flatMap((step) => {
      const label = turn.stepLabels.get(step.id);
      if (!label || label.said || step.status !== "active") return [];
      return label.relabelled || at - label.since >= VOICE_MODE.progressHoldS * 1000 ? [label] : [];
    });
    if (due.length === 0 && turn.progressQueue.length === 0) return;
    due.forEach((label) => {
      label.said = true;
    });
    const lines = [...due.map((label) => label.text), ...turn.progressQueue.splice(0)];
    sendAppend(session, "session.thinking.append", delegationFor(turn), VOICE_NOTES.progress(lines.join(" ")));
    endOpening(session);
    voiceLog("progress.said", { traceId: turn.traceId, steps: due.length, lines: lines.length });
  };

  const noteUserStop = () => {
    const turn = turnRef.current;
    if (!turn || !turn.open || turn.stopped) return;
    turn.stopped = true;
    turn.says = [];
    turn.next = null;
    turn.progressQueue = [];
    if (turn.delegation) {
      turn.delegation = { ...turn.delegation, stoppedByUser: true };
      writeDelegation(turn.liveSessionId, turn.delegation);
    }
    const session = sessionRef.current;
    if (session) sendAppend(session, "session.thinking.append", delegationFor(turn), VOICE_NOTES.stopped);
    voiceLog("request.stopped", { traceId: turn.traceId });
    if (!turn.voice) return;
    if (session) session.stats.stopped += 1;
    triggerGAEvent(GA_LABEL_ENUMS.home_chat_voice_request_stopped, "", "", "", { source: requestSource(turn) });
  };

  const requestOpened = (request: ChatRequest) => {
    if (request.requestType === VOICE_EXCHANGE) return;
    setVoiceRequestOpen(request.voice === true);
    finishNarration(turnRef.current);
    turnRef.current = {
      traceId: request.traceId,
      voice: request.voice,
      open: true,
      stopped: false,
      narrated: false,
      delegation: null,
      liveSessionId: null,
      userEntryId: null,
      says: [],
      next: null,
      handOffAt: now(),
      lastSaySentAt: null,
      words: [],
      progressSeen: 0,
      progressQueue: [],
      stepLabels: new Map(),
      progressDone: false,
      failureSaid: false,
      failed: false,
      heard: [],
      draft: "",
      clean: null,
      cleaner: null,
      reply: null,
      held: null,
      late: [],
      timing: null,
      lastCase: null,
    };
    if (sessionRef.current) sessionRef.current.lastActivityAt = now();
  };

  const requestFailed = (request: ChatRequest) => {
    const turn = turnRef.current;
    if (!turn || turn.traceId !== request.traceId || !turn.voice || turn.stopped || turn.narrated) return;
    const first = !turn.failed;
    turn.failed = true;
    voiceLog("request.failed", { traceId: turn.traceId, xeniaSaidIt: turn.failureSaid });
    if (first) {
      if (sessionRef.current) sessionRef.current.stats.failed += 1;
      triggerGAEvent(GA_LABEL_ENUMS.home_chat_voice_request_failed, "", "", "", {
        source: requestSource(turn),
        xenia_said_it: turn.failureSaid,
      });
    }
    if (!turn.failureSaid) turn.says.push(VOICE_NOTES.failed);
    const fixed = turn.clean && turn.cleaner ? turn.cleaner.cleaned(turn.clean.fragments) : null;
    const requestText = turn.clean ? (fixed?.complete ? fixed.text : turn.clean.raw) : null;
    live.current.state.setChatData((prev: iChatData[]) => withFailedExchange(prev, turn.userEntryId, requestText));
  };

  const requestClosed = (request: ChatRequest) => {
    if (request.requestType === VOICE_EXCHANGE) return;
    setVoiceRequestOpen(false);
    const turn = turnRef.current;
    const draft = turn && turn.traceId === request.traceId ? turn.draft : "";
    if (turn && turn.traceId === request.traceId) turn.open = false;
    const fix = turn && turn.traceId === request.traceId ? turn.clean : null;
    const fixed = fix && turn?.cleaner ? turn.cleaner.cleaned(fix.fragments) : null;
    if (fix && fixed?.complete && fixed.text !== fix.raw) {
      live.current.state.setChatData((prev: iChatData[]) => withCleanPrompts(prev, fix.raw, fixed.text));
    }
    if (fix && !fixed?.complete) {
      const target = sessionRef.current?.targets.find((candidate) => candidate.fragments === fix.fragments);
      if (target) target.sent = fix.raw;
    }
    if (sessionRef.current) sessionRef.current.lastActivityAt = now();
    else finishNarration(turn);
    setTimeout(() => {
      if (draft) live.current.state.setChatInputText(draft);
      handlers.current.flushDeferred();
    }, 0);
  };

  const requestMasked = (request: MaskedRequest) => {
    const turn = turnRef.current;
    if (!turn || turn.traceId !== request.traceId) return;
    turn.clean = null;
    voiceLog("request.masked", { traceId: request.traceId });
  };

  const voiceFrame = (frame: VoiceFrame, replyKind: string | null) => {
    const session = sessionRef.current;
    const turn = turnRef.current;
    if (!session || !turn || !turn.voice || turn.narrated) return;
    const plan = planVoiceFrame(frame);
    if (typeof frame?.case === "string") turn.lastCase = frame.case;
    if (turn.timing && turn.timing.frameMs === undefined) {
      turn.timing.frameMs = now() - turn.timing.at;
      voiceLog("frame.first", { traceId: turn.traceId, case: frame?.case, ms: turn.timing.frameMs });
    }
    session.lastActivityAt = now();
    if (plan.endsProgress && !turn.progressDone) {
      turn.progressDone = true;
      turn.progressQueue = [];
      voiceLog("progress.done", { traceId: turn.traceId, case: frame?.case });
    }
    if (plan.failed) turn.failureSaid = true;
    if (turn.stopped) return;
    plan.know.forEach((know) => sendAppend(session, "session.thinking.append", delegationFor(turn), know));
    if (frame?.case === "done" && replyKind === "itinerary" && turn.heard.some((fragment) => session.busy.has(fragment))) {
      voiceLog("done.skipped", { traceId: turn.traceId, says: plan.say.length });
      return;
    }
    turn.says.push(...plan.say);
    pumpSays(session);
  };

  const typedTurn = (turn: TypedTurn) => {
    const session = sessionRef.current;
    if (!session) return;
    adoptChatId(session.id);
    if (session.startedAt === null) return;
    endOpening(session);
    session.lastActivityAt = now();
    session.stats.typed += 1;
    sendAppend(session, "session.thinking.append", null, VOICE_NOTES.typed(turn.text.slice(0, 1000)));
    writeLine(session, {
      speaker: "user",
      mode: "typed",
      handledBy: "gpt-live",
      text: turn.text,
      atMs: now() - session.startedAt,
      delegationId: null,
      traceId: turn.traceId,
    });
  };

  const sendChip = (text: string) => {
    const session = sessionRef.current;
    const { poc: sender } = live.current;
    if (!session || session.startedAt === null) {
      sender.handleTopDestinationSendMessage(text);
      return;
    }
    const before = turnRef.current;
    const newChat = live.current.state.chatData.length === 0;
    const sent = sender.handleTopDestinationSendMessage(text, false, false, { voice: true });
    const turn = turnRef.current;
    if (!turn || turn === before) return;
    endOpening(session);
    session.stats.chips += 1;
    triggerGAEvent(GA_LABEL_ENUMS.home_chat_voice_request_sent, "", "", "", { source: "chip", new_chat: newChat });
    sendAppend(session, "session.thinking.append", null, VOICE_NOTES.chip(text));
    writeLine(session, {
      speaker: "user",
      mode: "typed",
      handledBy: "gpt-live",
      text,
      atMs: now() - session.startedAt,
      delegationId: null,
      traceId: turn.traceId,
    });
    turn.liveSessionId = session.id;
    turn.cleaner = cleanerOf(session);
    void Promise.resolve(sent).then((result: any) => {
      if (result?.userEntryId) turn.userEntryId = result.userEntryId;
    });
  };

  const checkoutNote = (text: string) => {
    const session = sessionRef.current;
    if (session) sendAppend(session, "session.thinking.append", null, text);
  };

  return { delegationFor, finishNarration, pumpSays, closeNarration, pumpProgress, noteUserStop, requestOpened, requestFailed, requestClosed, requestMasked, voiceFrame, typedTurn, sendChip, checkoutNote };
};

export type Narration = ReturnType<typeof createNarration>;
